import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { getTheme, THEME_MODES, ThemeMode } from './theme';
import { Theme } from '@mui/material/styles';

interface ThemeContextType {
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  theme: Theme;
}

// Theme context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Local storage key for theme preference
const THEME_STORAGE_KEY = 'posimat-theme-mode';

// Theme provider component
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get initial theme from localStorage or default to dark
  const getInitialTheme = (): ThemeMode => {
    try {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme === THEME_MODES.LIGHT || savedTheme === THEME_MODES.DARK) {
        return savedTheme;
      }
      return THEME_MODES.DARK;
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
      return THEME_MODES.DARK;
    }
  };

  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialTheme);

  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [themeMode]);

  // Toggle between light and dark theme
  const toggleTheme = useCallback(() => {
    setThemeMode((prevMode) =>
      prevMode === THEME_MODES.LIGHT ? THEME_MODES.DARK : THEME_MODES.LIGHT
    );
  }, []);

  // Get current theme object
  const currentTheme = useMemo(() => getTheme(themeMode), [themeMode]);

  // Context value
  const contextValue = useMemo<ThemeContextType>(() => ({
    themeMode,
    toggleTheme,
    setThemeMode,
    theme: currentTheme,
  }), [themeMode, toggleTheme, currentTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={currentTheme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

export { THEME_MODES };

export default ThemeProvider;